<template>
  <el-dialog
    v-model="visible"
    class="watermark-dialog"
    modal-class="watermark-dialog-overlay"
    width="590px"
    align-center
    :show-close="false"
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <div class="watermark-dialog-shell">
      <!-- Header -->
      <div class="watermark-dialog-header">
        <h3 class="watermark-dialog-title">AI生成水印设置</h3>
        <button type="button" class="watermark-dialog-close" aria-label="关闭" @click="handleClose">
          <SvgIcon name="modal-close" :size="10" class="watermark-dialog-close__desktop" />
          <SvgIcon
            name="character-menu-collapse"
            :size="12"
            class="watermark-dialog-close__mobile"
          />
        </button>
      </div>

      <!-- Content -->
      <div class="watermark-dialog-body">
        <p class="watermark-dialog-text">
          根据法律法规要求，云展绘本平台（"平台"）提供AI生成合成服务，可能导致公众混淆或者误认的，平台应当在AI生成合成内容上添加显式标识（即"AI生成"明水印），向公众进行提示。经过您的申请，平台可以向您提供未添加显式标识的AI生成合成内容。如您后续使用网络信息内容传播服务发布AI生成合成内容，请注意您还需主动声明并使用传播平台提供的标识功能进行标识。您理解并承诺，如您未按照法律法规要求在AI生成合成内容上添加显式标识，导致公众混淆或者误认，因此所发生的后果和责任均由您自行承担。
        </p>
        <p class="watermark-dialog-text">
          打开去除水印开关后，您使用云展绘本前述账号所创作、生成的AI生成合成内容将不再添加"AI生成"明水印，但仍将保留品牌水印。
        </p>

        <!-- Toggle Card -->
        <div class="watermark-dialog-card">
          <div class="watermark-dialog-card-inner">
            <button
              type="button"
              class="watermark-switch"
              :class="{ 'watermark-switch--on': localEnableRemoval }"
              :aria-label="localEnableRemoval ? '已开启去除水印' : '未开启去除水印'"
              @click="localEnableRemoval = !localEnableRemoval"
            >
              <span class="watermark-switch-knob" />
            </button>
            <div class="watermark-toggle-text">
              <label class="watermark-toggle-label">去除水印</label>
              <p class="watermark-toggle-hint">
                打开开关并点击保存设置，代表您已确认充分了解上述情况并同意<br />后续可以在侧边栏-&gt;AI生成水印设置里面修改水印设置
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="watermark-dialog-footer">
        <button type="button" class="watermark-save-btn" :disabled="saving" @click="handleSave">
          {{ saving ? '保存中...' : '保存设置' }}
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { ElMessage } from 'element-plus';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { useUserStore } from '@/store';

  const userStore = useUserStore();

  const props = defineProps<{
    modelValue: boolean;
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    close: [];
    saved: [];
  }>();

  const visible = ref(false);
  const localEnableRemoval = ref(false);
  const saving = ref(false);

  watch(
    () => props.modelValue,
    async (val) => {
      visible.value = val;
      if (val) {
        await loadSetting();
      }
    }
  );

  async function loadSetting() {
    try {
      await userStore.refreshMeta();
      localEnableRemoval.value = !userStore.userMeta.enable_watermark;
    } catch {
      // 获取失败保持默认
    }
  }

  function handleClose() {
    visible.value = false;
    emit('update:modelValue', false);
    emit('close');
  }

  function handleClosed() {
    // dialog 关闭动画结束后的清理
  }

  async function handleSave() {
    saving.value = true;
    try {
      const newEnableWatermark = !localEnableRemoval.value;
      await userStore.setMeta({ enable_watermark: newEnableWatermark });
      ElMessage.success('水印设置保存成功');
      emit('saved');
      handleClose();
    } catch {
      ElMessage.error('保存失败，请稍后重试');
    } finally {
      saving.value = false;
    }
  }
</script>

<style scoped>
  :global(.watermark-dialog.el-dialog) {
    padding: 0;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }

  :global(.watermark-dialog.el-dialog .el-dialog__body) {
    padding: 0;
  }

  :global(.watermark-dialog.el-dialog .el-dialog__header),
  :global(.watermark-dialog.el-dialog .el-dialog__footer) {
    display: none;
  }

  .watermark-dialog-shell {
    display: flex;
    flex-direction: column;
    padding: 24px;
  }

  /* Header */
  .watermark-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 24px;
  }

  .watermark-dialog-title {
    margin: 0;
    font-size: 16px;
    font-weight: 400;
    color: #1d2129;
    line-height: 16px;
  }

  .watermark-dialog-close {
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #222529;
    cursor: pointer;
    flex-shrink: 0;
  }

  .watermark-dialog-close:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  .watermark-dialog-close__mobile {
    display: none;
  }

  /* Body */
  .watermark-dialog-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }

  .watermark-dialog-text {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    color: #1d2129;
    line-height: 1.8;
  }

  /* Toggle Card */
  .watermark-dialog-card {
    padding: 10px;
    border: 1px solid #efefef;
    border-radius: 8px;
    background: #ffffff;
  }

  .watermark-dialog-card-inner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .watermark-toggle-text {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .watermark-toggle-label {
    font-size: 14px;
    font-weight: 400;
    color: #1d2129;
    line-height: 16px;
    cursor: default;
  }

  .watermark-toggle-hint {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    color: #4e5969;
    line-height: 19px;
  }

  /* Toggle Switch */
  .watermark-switch {
    position: relative;
    width: 30px;
    height: 16px;
    padding: 0;
    border: none;
    border-radius: 12px;
    background: #c9cdd4;
    cursor: pointer;
    transition: background 0.2s;
    flex-shrink: 0;
  }

  .watermark-switch--on {
    background: #6033e1;
  }

  .watermark-switch-knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #ffffff;
    transition: transform 0.2s;
  }

  .watermark-switch--on .watermark-switch-knob {
    transform: translateX(14px);
  }

  /* Footer */
  .watermark-dialog-footer {
    display: flex;
    justify-content: flex-end;
  }

  .watermark-save-btn {
    width: 100px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: #1d2129;
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    line-height: 14px;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .watermark-save-btn:hover {
    opacity: 0.85;
  }

  .watermark-save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    :global(.watermark-dialog-overlay) {
      background: rgba(0, 0, 0, 0.3);
    }

    :global(.watermark-dialog-overlay .el-overlay-dialog) {
      align-items: flex-end;
    }

    :global(.watermark-dialog.el-dialog) {
      width: 100% !important;
      height: auto;
      max-height: calc(100dvh - max(16px, env(safe-area-inset-top)));
      margin: 0 !important;
      overflow: hidden;
      border-radius: 16px 16px 0 0;
      box-shadow: none;
    }

    .watermark-dialog-shell {
      max-height: calc(100dvh - max(16px, env(safe-area-inset-top)));
      box-sizing: border-box;
      padding: 16px 16px calc(20px + env(safe-area-inset-bottom));
      overflow: hidden;
    }

    .watermark-dialog-header {
      height: 24px;
      margin-bottom: 16px;
    }

    .watermark-dialog-title {
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 16px;
    }

    .watermark-dialog-close {
      width: 44px;
      height: 24px;
      border: 1px solid #eff0f7;
      border-radius: 66px;
      background: #ffffff;
      color: #160211;
    }

    .watermark-dialog-close:hover {
      background: #ffffff;
    }

    .watermark-dialog-close__desktop {
      display: none;
    }

    .watermark-dialog-close__mobile {
      display: block;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.4;
    }

    .watermark-dialog-body {
      flex: 0 1 auto;
      min-height: 0;
      gap: 16px;
      margin-bottom: 16px;
      overflow-x: hidden;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }

    .watermark-dialog-text {
      color: #1d2129;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 23px;
    }

    .watermark-dialog-card {
      min-height: 120px;
      box-sizing: border-box;
      padding: 16px;
      flex-shrink: 0;
    }

    .watermark-dialog-card-inner {
      gap: 7px;
    }

    .watermark-toggle-text {
      min-width: 0;
      gap: 8px;
    }

    .watermark-toggle-label {
      line-height: 16px;
    }

    .watermark-toggle-hint {
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 19px;
    }

    .watermark-dialog-footer {
      flex-shrink: 0;
    }

    .watermark-save-btn {
      width: 100%;
      flex-shrink: 0;
    }
  }
</style>
